import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification, DeleteConfirmationModal } from '../common';

const EmailProfilesSettings = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [checkingReplies, setCheckingReplies] = useState(false);
  const [testingIMAPConnection, setTestingIMAPConnection] = useState(false);
  const { showNotification } = useNotification();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    host: '',
    port: '587',
    username: '',
    password: '',
    from_email: '',
    from_name: '',
    is_secure: false,
    is_default: false,
    is_system: false,
    // IMAP settings for email reply tracking
    imap_host: '',
    imap_port: '993',
    imap_secure: true,
    enable_reply_tracking: false,
    reply_check_schedule: '*/10 * * * *'
  });
  
  useEffect(() => {
    fetchProfiles();
  }, []);
  
  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/settings/smtp-profiles', config);
      
      setProfiles(res.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching SMTP profiles:', err);
      setError('Failed to load SMTP profiles');
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      host: '',
      port: '587',
      username: '',
      password: '',
      from_email: '',
      from_name: '',
      is_secure: false,
      is_default: false,
      is_system: false,
      // IMAP settings
      imap_host: '',
      imap_port: '993',
      imap_secure: true,
      enable_reply_tracking: false,
      reply_check_schedule: '*/10 * * * *'
    });
    setEditingProfileId(null);
  };
  
  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
    setTestResult(null);
  };
  
  const handleEdit = (profile) => {
    setFormData({
      name: profile.name,
      description: profile.description || '',
      host: profile.host,
      port: profile.port.toString(),
      username: profile.username,
      password: '••••••••', // Masked for security
      from_email: profile.from_email,
      from_name: profile.from_name || '',
      is_secure: profile.is_secure,
      is_default: profile.is_default,
      is_system: profile.is_system,
      // IMAP settings
      imap_host: profile.imap_host || '',
      imap_port: profile.imap_port ? profile.imap_port.toString() : '993',
      imap_secure: profile.imap_secure !== false, // Default to true if not set
      enable_reply_tracking: !!profile.enable_reply_tracking,
      reply_check_schedule: profile.reply_check_schedule || '*/10 * * * *'
    });
    setEditingProfileId(profile.id);
    setShowForm(true);
    setTestResult(null);
  };
  
  const handleCancel = () => {
    setShowForm(false);
    resetForm();
    setTestResult(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      if (editingProfileId) {
        // Update existing profile
        await axios.put(
          `/api/settings/smtp-profiles/${editingProfileId}`,
          formData,
          config
        );
        
        showNotification('SMTP profile updated successfully', 'success');
      } else {
        // Create new profile
        await axios.post(
          '/api/settings/smtp-profiles',
          formData,
          config
        );
        
        showNotification('SMTP profile created successfully', 'success');
      }
      
      // Refresh the profiles list
      fetchProfiles();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Error saving SMTP profile:', err);
      showNotification(err.response?.data?.message || 'Error saving SMTP profile', 'error');
    }
  };
  
  const handleDelete = async (id) => {
    setProfileToDelete(id);
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete(`/api/settings/smtp-profiles/${profileToDelete}`, config);
      
      showNotification('SMTP profile deleted successfully', 'success');
      
      // Refresh the profiles list
      fetchProfiles();
      setShowDeleteConfirm(false);
    } catch (err) {
      console.error('Error deleting SMTP profile:', err);
      showNotification(err.response?.data?.message || 'Error deleting SMTP profile', 'error');
    }
  };
  
  // Manually check for email replies
  const checkEmailReplies = async () => {
    try {
      setCheckingReplies(true);
      setTestResult(null);
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.post('/api/email/check-replies', {}, config);
      
      setTestResult({
        success: true,
        message: res.data.message || 'Email check triggered successfully',
        debugInfo: res.data.debugLog || JSON.stringify(res.data.result, null, 2)
      });
      
      showNotification('Email reply check completed', 'success');
    } catch (err) {
      console.error('Error checking email replies:', err);
      
      // Get detailed error message
      const errorMessage = err.response?.data?.message || 'Failed to check email replies';
      const helpText = err.response?.data?.help || '';
      const details = err.response?.data?.details || '';
      const debugLog = err.response?.data?.debugLog || '';
      
      // Create a more detailed message for the notification
      const fullErrorMessage = [errorMessage, helpText, details].filter(Boolean).join(' - ');
      
      setTestResult({
        success: false,
        message: fullErrorMessage,
        debugInfo: debugLog || JSON.stringify(err.response?.data?.error || err.message, null, 2)
      });
      
      showNotification(fullErrorMessage, 'error');
    } finally {
      setCheckingReplies(false);
    }
  };
  
  // Debug email reply service
  const debugReplyService = async () => {
    try {
      setTestResult(null);
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/email/debug-reply-service', config);
      
      // Format the debug info for display
      const debugInfo = JSON.stringify(res.data, null, 2);
      
      setTestResult({
        success: true,
        message: 'Debug information retrieved:',
        debugInfo: debugInfo
      });
      
      console.log('Email reply service debug info:', res.data);
    } catch (err) {
      console.error('Error retrieving debug information:', err);
      
      setTestResult({
        success: false,
        message: 'Error retrieving debug information: ' + (err.response?.data?.message || err.message)
      });
    }
  };
  
  // Test IMAP connection using imap-simple
  const testImapSimple = async () => {
    try {
      setTestResult(null);
      setTestingId('imap-simple');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      };
      
      // Find the first profile with reply tracking enabled
      const enabledProfile = profiles.find(p => p.enable_reply_tracking);
      
      if (!enabledProfile) {
        throw new Error('No profiles with reply tracking enabled found');
      }
      
      const res = await axios.post('/api/email/test-imap-simple', 
        { profileId: enabledProfile.id }, 
        config
      );
      
      setTestResult({
        success: true,
        message: 'IMAP Connection Test (Using imap-simple)',
        debugInfo: JSON.stringify(res.data, null, 2)
      });
      
      showNotification('IMAP connection successful!', 'success');
    } catch (err) {
      console.error('Error testing IMAP connection:', err);
      
      // Get detailed error message
      const errorMessage = err.response?.data?.message || 'Failed to test IMAP connection';
      const helpText = err.response?.data?.help || '';
      const details = err.response?.data?.details || '';
      
      // Create a more detailed message
      const fullErrorMessage = [errorMessage, helpText].filter(Boolean).join(' - ');
      
      setTestResult({
        success: false,
        message: 'IMAP Connection Test Failed (Using imap-simple)',
        debugInfo: JSON.stringify({
          error: fullErrorMessage,
          details: details || err.message
        }, null, 2)
      });
      
      showNotification(fullErrorMessage, 'error');
    } finally {
      setTestingId(null);
    }
  };
  
  const testConnection = async (id, type = 'smtp') => {
    try {
      setTestingId(id);
      setTestResult(null);
      setError('');
      
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      let endpoint;
      
      if (type === 'imap') {
        endpoint = `/api/settings/smtp-profiles/${id}/test-imap`;
      } else {
        endpoint = id
          ? `/api/settings/smtp-profiles/${id}/test`
          : '/api/email/test-connection';
      }
      
      const res = await axios.get(endpoint, config);
      
      setTestResult({
        success: true,
        message: res.data.message || 'Connection successful'
      });
      
      setTestingId(null);
    } catch (err) {
      console.error(`Error testing ${type.toUpperCase()} connection:`, err);
      
      setTestResult({
        success: false,
        message: err.response?.data?.message || 'Connection failed'
      });
      
      setTestingId(null);
    }
  };
  
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-full mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-full mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-full mb-3"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Email SMTP Profiles</h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage SMTP profiles for sending emails from different accounts
          </p>
        </div>
        <div className="flex space-x-2">
          {profiles.some(p => p.enable_reply_tracking) && (
            <>
              <button
                onClick={checkEmailReplies}
                disabled={checkingReplies}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {checkingReplies ? 'Checking Replies...' : 'Check Email Replies'}
              </button>
              <button
                onClick={debugReplyService}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                title="Get detailed information about the email reply service"
              >
                Debug Service
              </button>
              <button
                onClick={testImapSimple}
                disabled={testingId === 'imap-simple'}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                title="Test IMAP connection using alternative library"
              >
                {testingId === 'imap-simple' ? 'Testing...' : 'Test IMAP (Alternative)'}
              </button>
            </>
          )}
          <button
            onClick={handleAddNew}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add New Profile
          </button>
        </div>
      </div>
      
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
          {success}
        </div>
      )}
      
      {testResult && (
        <div className={`mb-4 ${
          testResult.success 
            ? 'bg-green-50 border border-green-200 text-green-600'
            : 'bg-red-50 border border-red-200 text-red-600'
          } px-4 py-3 rounded`}
        >
          <div className="flex items-center">
            {testResult.success ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span>{testResult.message}</span>
          </div>
          
          {/* Display debug info if available */}
          {testResult.debugInfo && (
            <div className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-60 text-gray-800 text-xs font-mono">
              <pre>{testResult.debugInfo}</pre>
            </div>
          )}
        </div>
      )}
      
      {showForm ? (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingProfileId ? 'Edit SMTP Profile' : 'New SMTP Profile'}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  id="description"
                  name="description"
                  type="text"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="host" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Host <span className="text-red-500">*</span>
                </label>
                <input
                  id="host"
                  name="host"
                  type="text"
                  value={formData.host}
                  onChange={handleInputChange}
                  placeholder="smtp.example.com"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="port" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Port <span className="text-red-500">*</span>
                </label>
                <input
                  id="port"
                  name="port"
                  type="text"
                  value={formData.port}
                  onChange={handleInputChange}
                  placeholder="587"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex items-center pt-7">
                <input
                  id="is_secure"
                  name="is_secure"
                  type="checkbox"
                  checked={formData.is_secure}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_secure" className="ml-2 block text-sm text-gray-700">
                  Use Secure Connection (TLS/SSL)
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Username <span className="text-red-500">*</span>
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="your_username"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={formData.password === '••••••••' ? 'Enter new password to update' : 'Enter password'}
                  required={!editingProfileId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {formData.password === '••••••••' && (
                  <p className="mt-1 text-xs text-gray-500">
                    <span className="font-medium">Important:</span> To update the password, you must enter a completely new value. The masked field will not update the password unless changed.
                  </p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="from_email" className="block text-sm font-medium text-gray-700 mb-1">
                  From Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="from_email"
                  name="from_email"
                  type="email"
                  value={formData.from_email}
                  onChange={handleInputChange}
                  placeholder="noreply@yourcompany.com"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="from_name" className="block text-sm font-medium text-gray-700 mb-1">
                  From Name
                </label>
                <input
                  id="from_name"
                  name="from_name"
                  type="text"
                  value={formData.from_name}
                  onChange={handleInputChange}
                  placeholder="Your Company Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* IMAP Settings Section */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center">
                <span>Email Reply Tracking (IMAP Settings)</span>
                <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Optional</span>
              </h4>
              
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="enable_reply_tracking"
                    checked={formData.enable_reply_tracking}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable email reply tracking</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 pl-6">
                  When enabled, the system will check for replies to emails sent from this profile
                </p>
              </div>
              
              {formData.enable_reply_tracking && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label htmlFor="imap_host" className="block text-sm font-medium text-gray-700 mb-1">
                        IMAP Host <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="imap_host"
                        name="imap_host"
                        type="text"
                        value={formData.imap_host}
                        onChange={handleInputChange}
                        placeholder="imap.example.com"
                        required={formData.enable_reply_tracking}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="imap_port" className="block text-sm font-medium text-gray-700 mb-1">
                        IMAP Port <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="imap_port"
                        name="imap_port"
                        type="text"
                        value={formData.imap_port}
                        onChange={handleInputChange}
                        placeholder="993"
                        required={formData.enable_reply_tracking}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div className="flex items-center pt-7">
                      <input
                        id="imap_secure"
                        name="imap_secure"
                        type="checkbox"
                        checked={formData.imap_secure}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="imap_secure" className="ml-2 block text-sm text-gray-700">
                        Use Secure Connection (TLS/SSL)
                      </label>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="reply_check_schedule" className="block text-sm font-medium text-gray-700 mb-1">
                      Check Schedule (Cron Format)
                    </label>
                    <div className="flex items-center">
                      <input
                        id="reply_check_schedule"
                        name="reply_check_schedule"
                        type="text"
                        value={formData.reply_check_schedule}
                        onChange={handleInputChange}
                        placeholder="*/10 * * * *"
                        className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="ml-3 text-xs text-gray-500">
                        Default: every 10 minutes (*/10 * * * *)
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Cron format: minute hour day month weekday (e.g., "0 * * * *" for once every hour at minute 0)
                    </p>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                    <p>
                      <strong>Note:</strong> The system will use your SMTP credentials for IMAP access. 
                      Make sure the credentials have permission to read emails via IMAP.
                    </p>
                    {editingProfileId && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => testConnection(editingProfileId, 'imap')}
                          disabled={testingId === editingProfileId}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {testingId === editingProfileId ? 'Testing...' : 'Test IMAP Connection'}
                        </button>
                        <span className="ml-2 text-xs">Test the IMAP settings before saving</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-4 mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_default"
                  checked={formData.is_default}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Set as default profile</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_system"
                  checked={formData.is_system}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Use for system notifications</span>
              </label>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {editingProfileId ? 'Update Profile' : 'Create Profile'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white overflow-hidden shadow rounded-lg border mb-6">
          {profiles.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No SMTP profiles found. Click "Add New Profile" to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Host/Port
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      From Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {profiles.map((profile) => (
                    <tr key={profile.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{profile.name}</div>
                            {profile.description && (
                              <div className="text-sm text-gray-500">{profile.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{profile.host}</div>
                        <div className="text-sm text-gray-500">
                          Port: {profile.port} {profile.is_secure ? '(Secure)' : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{profile.username}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{profile.from_email}</div>
                        {profile.from_name && (
                          <div className="text-sm text-gray-500">{profile.from_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {profile.is_default && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Default
                            </span>
                          )}
                          {profile.is_system && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              System
                            </span>
                          )}
                          {profile.enable_reply_tracking && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                              Reply Tracking
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => testConnection(profile.id, 'smtp')}
                            disabled={testingId === profile.id}
                            className="text-blue-600 hover:text-blue-900"
                            title="Test SMTP connection"
                          >
                            {testingId === profile.id ? 'Testing...' : 'Test SMTP'}
                          </button>
                          {profile.enable_reply_tracking && (
                            <button
                              onClick={() => testConnection(profile.id, 'imap')}
                              disabled={testingId === profile.id}
                              className="text-green-600 hover:text-green-900"
                              title="Test IMAP connection for reply tracking"
                            >
                              {testingId === profile.id ? 'Testing...' : 'Test IMAP'}
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(profile)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(profile.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this SMTP profile? This action cannot be undone."
      />
    </div>
  );
};

export default EmailProfilesSettings;